import { shouldForceLogout } from '@/lib/auth-redirect'
import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios'

/**
 * 后端统一响应壳（见 docs/02-技术设计/00-总体设计/13-MVP接口设计.md §1）：
 *   { code: "0", message: "success", data: T }
 *
 * 约定：
 *   - code === "0"  视为业务成功，返回 data
 *   - 其它 code     作为业务错误，在 interceptor 里抛出
 *   - HTTP 4xx/5xx  作为网络/服务错误，带 url + status
 */
export interface ApiEnvelope<T> {
  code: string
  message: string
  data: T
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly httpStatus?: number,
    public readonly url?: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

const TOKEN_KEY = 'ai_agent_token'

type HttpClient = Omit<AxiosInstance, 'get' | 'post' | 'put' | 'patch' | 'delete'> & {
  get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T>
  post<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T>
  put<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T>
  patch<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T>
  delete<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T>
}

export function setAuthToken(token: string | null) {
  if (token) localStorage.setItem(TOKEN_KEY, token)
  else localStorage.removeItem(TOKEN_KEY)
}

export function getAuthToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

function createClient(): HttpClient {
  const instance = axios.create({
    baseURL: '/api/v1',
    timeout: 30_000,
    headers: {
      'Content-Type': 'application/json',
    },
  })

  // 请求拦截：注入 Bearer
  instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const token = getAuthToken()
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  })

  // 响应拦截：脱壳 / 统一错误
  instance.interceptors.response.use(
    (resp) => {
      const body = resp.data as ApiEnvelope<unknown>
      if (body && typeof body === 'object' && 'code' in body) {
        if (body.code === '0') {
          // 把 data 直接返回，调用方就不用再写 .data.data
          return body.data
        }
        throw new ApiError(body.message || '请求失败', body.code, resp.status, resp.config.url)
      }
      return resp
    },
    (error) => {
      const status = error?.response?.status
      const url = error?.config?.url
      const msg = error?.response?.data?.message || error?.message || '网络错误'
      if (shouldForceLogout(status, window.location.pathname)) {
        setAuthToken(null)
        localStorage.removeItem('ai-agent-auth')
        const currentPath = window.location.pathname
        if (currentPath !== '/login') {
          window.location.replace(`/login?redirect=${encodeURIComponent(currentPath)}`)
        }
      }
      return Promise.reject(new ApiError(msg, `HTTP_${status || 'ERR'}`, status, url))
    },
  )

  return instance as HttpClient
}

export const http = createClient()
